import { useMemo } from 'react';
import { Extension } from '@codemirror/state';
import { EditorView, hoverTooltip, Tooltip } from '@codemirror/view';
import { syntaxTree } from '@codemirror/language';

type HoverDocsParams = {
  enabled?: boolean;
  code?: string;
};

// Extract JSDoc comment above a position
function extractJsDoc(code: string, line: number): string | null {
  const lines = code.split('\n');
  const comments: string[] = [];

  // Look for JSDoc comment above the current line
  let i = line - 2; // line is 1-indexed, array is 0-indexed, so line - 2 is the line above
  while (i >= 0) {
    const trimmed = lines[i].trim();

    // End of JSDoc
    if (trimmed.startsWith('*/')) {
      i--;
      continue;
    }

    // Inside JSDoc
    if (trimmed.startsWith('*')) {
      comments.unshift(trimmed.replace(/^\*\s*/, ''));
      i--;
      continue;
    }

    // Start of JSDoc
    if (trimmed.startsWith('/**')) {
      break;
    }

    // Single line comment
    if (trimmed.startsWith('//')) {
      comments.unshift(trimmed.replace(/^\/\/\s*/, ''));
      i--;
      continue;
    }

    // Not a comment, stop
    break;
  }

  return comments.length > 0 ? comments.join('\n') : null;
}

// Parse type annotation from code
function extractTypeInfo(code: string, symbolName: string): string | null {
  // Look for type annotations
  const patterns = [
    // const name: Type =
    new RegExp(`(?:const|let|var)\\s+${symbolName}\\s*:\\s*([^=;]+?)\\s*=`),
    // function name(params): ReturnType
    new RegExp(`function\\s+${symbolName}\\s*\\([^)]*\\)\\s*:\\s*([^{]+?)\\s*\\{`),
    // Arrow function const name: Type = () =>
    new RegExp(`const\\s+${symbolName}\\s*:\\s*([^=]+?)\\s*=`),
    // Parameter type (param: Type)
    new RegExp(`${symbolName}\\s*:\\s*([^,)]+)`),
  ];

  for (const pattern of patterns) {
    const match = code.match(pattern);
    if (match && match[1]) {
      return match[1].trim();
    }
  }

  return null;
}

// Get symbol kind from context
function getSymbolKind(nodeName: string): string {
  const kindMap: Record<string, string> = {
    VariableDeclaration: 'variable',
    VariableName: 'variable',
    FunctionDeclaration: 'function',
    FunctionExpression: 'function',
    ArrowFunction: 'function',
    ClassDeclaration: 'class',
    MethodDeclaration: 'method',
    PropertyDeclaration: 'property',
    TypeAliasDeclaration: 'type',
    InterfaceDeclaration: 'interface',
    EnumDeclaration: 'enum',
    ImportDeclaration: 'import',
    ExportDeclaration: 'export',
    PropertyName: 'property',
    PropertyDefinition: 'property',
  };

  return kindMap[nodeName] || 'symbol';
}

// Create hover tooltip content
function createTooltipContent(
  symbolName: string,
  kind: string,
  typeInfo: string | null,
  jsDoc: string | null
): HTMLElement {
  const container = document.createElement('div');
  container.className = 'hover-doc-tooltip';

  // Header with symbol name and kind
  const header = document.createElement('div');
  header.className = 'hover-doc-header';

  const kindBadge = document.createElement('span');
  kindBadge.className = `hover-doc-kind ${kind}`;
  kindBadge.textContent = kind;
  header.appendChild(kindBadge);

  const name = document.createElement('span');
  name.className = 'hover-doc-name';
  name.textContent = symbolName;
  header.appendChild(name);

  container.appendChild(header);

  // Type info
  if (typeInfo) {
    const typeEl = document.createElement('div');
    typeEl.className = 'hover-doc-type';
    typeEl.innerHTML = `<code>${escapeHtml(typeInfo)}</code>`;
    container.appendChild(typeEl);
  }

  // JSDoc description
  if (jsDoc) {
    const docEl = document.createElement('div');
    docEl.className = 'hover-doc-description';

    // Parse JSDoc tags
    const lines = jsDoc.split('\n');
    const description: string[] = [];
    const params: { name: string; type?: string; desc: string }[] = [];
    let returns: { type?: string; desc: string } | null = null;

    for (const line of lines) {
      const paramMatch = line.match(/@param\s+(?:\{([^}]+)\})?\s*(\w+)\s*(.*)/);
      const returnMatch = line.match(/@returns?\s+(?:\{([^}]+)\})?\s*(.*)/);

      if (paramMatch) {
        params.push({
          type: paramMatch[1],
          name: paramMatch[2],
          desc: paramMatch[3],
        });
      } else if (returnMatch) {
        returns = {
          type: returnMatch[1],
          desc: returnMatch[2],
        };
      } else if (!line.startsWith('@')) {
        description.push(line);
      }
    }

    // Render description
    if (description.length > 0) {
      const descText = document.createElement('p');
      descText.textContent = description.join(' ').trim();
      docEl.appendChild(descText);
    }

    // Render params
    if (params.length > 0) {
      const paramsEl = document.createElement('div');
      paramsEl.className = 'hover-doc-params';
      paramsEl.innerHTML =
        '<strong>Parameters:</strong>' +
        params
          .map(
            (p) =>
              `<div class="hover-doc-param">` +
              `<span class="param-name">${escapeHtml(p.name)}</span>` +
              (p.type ? `<span class="param-type">: ${escapeHtml(p.type)}</span>` : '') +
              (p.desc ? ` - ${escapeHtml(p.desc)}` : '') +
              `</div>`
          )
          .join('');
      docEl.appendChild(paramsEl);
    }

    // Render returns
    if (returns) {
      const returnsEl = document.createElement('div');
      returnsEl.className = 'hover-doc-returns';
      returnsEl.innerHTML =
        `<strong>Returns:</strong> ` +
        (returns.type ? `<span class="param-type">${escapeHtml(returns.type)}</span>` : '') +
        (returns.desc ? ` - ${escapeHtml(returns.desc)}` : '');
      docEl.appendChild(returnsEl);
    }

    container.appendChild(docEl);
  }

  return container;
}

function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (m) => {
    const map: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    };
    return map[m];
  });
}

export function useHoverDocs({ enabled = true, code = '' }: HoverDocsParams = {}) {
  const hoverDocsExtension = useMemo<Extension>(() => {
    if (!enabled) return [];

    return hoverTooltip(
      (view: EditorView, pos: number): Tooltip | null => {
        // Get the word at position
        const { from, text } = view.state.doc.lineAt(pos);
        const lineText = text;
        const linePos = pos - from;

        // Find word boundaries
        let wordStart = linePos;
        let wordEnd = linePos;

        while (wordStart > 0 && /[\w$]/.test(lineText[wordStart - 1])) {
          wordStart--;
        }
        while (wordEnd < lineText.length && /[\w$]/.test(lineText[wordEnd])) {
          wordEnd++;
        }

        if (wordStart === wordEnd) return null;

        const symbolName = lineText.slice(wordStart, wordEnd);

        // Skip keywords and common tokens
        const keywords = [
          'if',
          'else',
          'for',
          'while',
          'return',
          'const',
          'let',
          'var',
          'function',
          'class',
          'interface',
          'type',
          'import',
          'export',
          'from',
          'default',
          'async',
          'await',
          'new',
          'this',
          'true',
          'false',
          'null',
          'undefined',
          'typeof',
          'instanceof',
        ];
        if (keywords.includes(symbolName)) return null;

        // Get syntax tree node
        const tree = syntaxTree(view.state);
        const node = tree.resolveInner(from + wordStart, 1);
        const kind = getSymbolKind(node.parent?.name || node.name);

        // Get the line number
        const lineNumber = view.state.doc.lineAt(pos).number;

        // Extract type info and JSDoc
        const fullCode = code || view.state.doc.toString();
        const typeInfo = extractTypeInfo(fullCode, symbolName);
        const jsDoc = extractJsDoc(fullCode, lineNumber);

        // Only show tooltip if we have useful info
        if (!typeInfo && !jsDoc) {
          // At minimum show the kind
          if (kind === 'symbol') return null;
        }

        return {
          pos: from + wordStart,
          end: from + wordEnd,
          above: true,
          create: () => ({
            dom: createTooltipContent(symbolName, kind, typeInfo, jsDoc),
          }),
        };
      },
      {
        hoverTime: 300, // Delay before showing tooltip
      }
    );
  }, [enabled, code]);

  return { hoverDocsExtension };
}
